//
// # EditWireLabelDialog
//
// Command-palette style dialog for editing a wire label in a schematic.
// Pre-fills the input with the current label text.
//

import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useWorkspace, useWorkspaceSnapshot } from "../context";

export interface EditWireLabelDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function EditWireLabelDialog({
  open,
  onClose,
}: EditWireLabelDialogProps) {
  const workspace = useWorkspace();
  const { editWireLabelDialog } = useWorkspaceSnapshot();

  // Form state
  const [labelText, setLabelText] = useState("");

  // Reset form when dialog opens with a new wire
  useEffect(() => {
    if (open && editWireLabelDialog) {
      setLabelText(editWireLabelDialog.currentLabel);
    }
  }, [open, editWireLabelDialog?.currentLabel]);

  const handleSubmit = useCallback(() => {
    if (!editWireLabelDialog) return;

    workspace.updateWireLabel(
      editWireLabelDialog.designIdent,
      editWireLabelDialog.wireId,
      labelText.trim()
    );

    onClose();
  }, [editWireLabelDialog, labelText, workspace, onClose]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [handleSubmit, onClose]
  );

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!open) return null;

  return createPortal(
    <div
      className="command-palette-backdrop"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div className="command-palette add-instance-dialog" role="dialog">
        <div className="command-palette-prompt-label">Edit Wire Label</div>
        <input
          className="command-palette-input"
          type="text"
          placeholder="Enter wire label (leave empty to remove)"
          value={labelText}
          onChange={(e) => {
            setLabelText(e.target.value);
          }}
          autoFocus
        />
        <div className="add-instance-footer">
          <span className="add-instance-hint">
            Enter to update · Escape to cancel
          </span>
          <button
            className="add-instance-btn"
            onClick={handleSubmit}
            disabled={!editWireLabelDialog}
          >
            Update
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
